import { IsArray, IsIn, IsObject, IsOptional, IsString, Matches, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';

/**
 * Canonical usage-rights status. Connectors coerce upstream DAM values into one of
 * these four labels via per-data-source `RightsFieldMap.status.valueMap`.
 */
export type RightsStatus = 'approved' | 'restricted' | 'expired' | 'unknown';

export const RIGHTS_STATUS_VALUES: readonly RightsStatus[] = ['approved', 'restricted', 'expired', 'unknown'] as const;

/**
 * Per-item usage-rights signal returned alongside each `/data-source/:slug/query`
 * result. All sub-fields optional; an item with no resolvable rights signal omits
 * the `rights` key entirely (see omit-when-empty rule in `rights.mapper.ts`).
 */
export class RightsInfo {
	@IsOptional()
	@IsString()
	@IsIn((RIGHTS_STATUS_VALUES as unknown) as string[])
	status?: RightsStatus;

	@IsOptional()
	@IsString()
	expiresAt?: string; // ISO 8601 date or datetime

	@IsOptional()
	@IsArray()
	@IsString({ each: true })
	restrictions?: string[];

	@IsOptional()
	@IsString()
	licenseType?: string;

	@IsOptional()
	@IsObject()
	raw?: Record<string, unknown>;
}

/**
 * Rejects path segments that would traverse the prototype chain. Used on every
 * `path` field in `RightsFieldMap` so an admin cannot save a mapping that
 * resolves to `__proto__.polluted`, `constructor.prototype.isAdmin`, etc.
 * Addresses the prototype-pollution risk in `ObjectUtils.resolveDotNotationPath`,
 * which uses a plain split-and-traverse with no segment validation.
 */
export const RIGHTS_PATH_PATTERN = /^(?!.*\b(?:__proto__|constructor|prototype)\b).+$/;

export const RIGHTS_PATH_MESSAGE = 'rightsFieldMap path may not include __proto__, constructor, or prototype segments';

export class RightsFieldEntry {
	@IsString()
	@Matches(RIGHTS_PATH_PATTERN, { message: RIGHTS_PATH_MESSAGE })
	path: string;
}

/**
 * Allowed values for `status.deriveFrom`. Currently only `'window'` is
 * supported — derives status from a usage date window (`startPath`/`endPath`)
 * against the request time:
 *   - before start  → 'restricted'  (not yet in window)
 *   - within window → 'approved'
 *   - after end     → 'expired'
 * Used for DAMs (e.g. Brandshare) that surface usage windows but no explicit
 * status enum.
 */
export type RightsStatusDerivation = 'window';

export class RightsStatusFieldEntry {
	/**
	 * Dot-notation path to an upstream status field. Optional when `deriveFrom`
	 * is set — a configured `path` (when it resolves) takes precedence over
	 * derivation, so admins can pin an explicit status when their DAM has one
	 * and fall back to window derivation when it doesn't.
	 */
	@IsOptional()
	@IsString()
	@Matches(RIGHTS_PATH_PATTERN, { message: RIGHTS_PATH_MESSAGE })
	path?: string;

	/**
	 * Maps upstream status values (as they appear in the DAM) to the canonical
	 * `RightsStatus`. The mapper falls through to `'unknown'` for values not
	 * present in the map; the original upstream value is preserved in `raw`.
	 */
	@IsOptional()
	@IsObject()
	valueMap?: Record<string, RightsStatus>;

	/**
	 * Opt-in derivation strategy when no explicit upstream status field exists.
	 * `'window'` requires `startPath` and/or `endPath` (at least one).
	 */
	@IsOptional()
	@IsString()
	@IsIn(['window'])
	deriveFrom?: RightsStatusDerivation;

	@IsOptional()
	@IsString()
	@Matches(RIGHTS_PATH_PATTERN, { message: RIGHTS_PATH_MESSAGE })
	startPath?: string;

	@IsOptional()
	@IsString()
	@Matches(RIGHTS_PATH_PATTERN, { message: RIGHTS_PATH_MESSAGE })
	endPath?: string;
}

/**
 * Per-data-source configuration that tells the rights mapper where each
 * sub-field of `RightsInfo` lives in the upstream DAM item shape. Lives on
 * `ConnectorConfig` and is edited via the data-source admin dialog.
 *
 * `path` values use dot-notation (e.g. `context.usage_rights`, `classification`,
 * `metadata.enterprise_12345.usageRights.licenseType`) and are resolved against
 * each raw item via `ObjectUtils.resolveDotNotationPath`. Paths that include
 * `__proto__`, `constructor`, or `prototype` segments are rejected at validation
 * time — see `RIGHTS_PATH_PATTERN`.
 */
export class RightsFieldMap {
	@IsOptional()
	@ValidateNested()
	@Type(() => RightsStatusFieldEntry)
	status?: RightsStatusFieldEntry;

	@IsOptional()
	@ValidateNested()
	@Type(() => RightsFieldEntry)
	expiresAt?: RightsFieldEntry;

	@IsOptional()
	@ValidateNested()
	@Type(() => RightsFieldEntry)
	restrictions?: RightsFieldEntry;

	@IsOptional()
	@ValidateNested()
	@Type(() => RightsFieldEntry)
	licenseType?: RightsFieldEntry;

	@IsOptional()
	@ValidateNested()
	@Type(() => RightsFieldEntry)
	raw?: RightsFieldEntry;
}
